import { type CoborrowerRequiredConfig, normalizeCoborrowerRequiredRecord } from 'config/borrowerFieldRequired.util'
import { BorrowerSeperator } from 'pages/LoanApplication/config'

const borrowerOrder = Object.values(BorrowerSeperator)

const labels: Record<BorrowerSeperator, string> = Object.fromEntries(
  borrowerOrder.map((sep, i) => [sep, `Borrower ${i + 1}`]),
) as Record<BorrowerSeperator, string>

type Props = {
  value: CoborrowerRequiredConfig
  primaryRequired: boolean
  onChange: (next: Record<BorrowerSeperator, boolean>) => void
  className?: string
}

/**
 * One checkbox per borrower slot (up to 8). Edits a full per-borrower `Record` and keeps
 * the primary "required" flag in sync with `Borrower 1`.
 */
export const CoborrowerRequiredByBorrowerFields = ({ value, primaryRequired, onChange, className = '' }: Props) => {
  const record = normalizeCoborrowerRequiredRecord(value, primaryRequired)

  const setKey = (sep: BorrowerSeperator, checked: boolean) => {
    onChange({ ...record, [sep]: checked })
  }

  const setAll = (checked: boolean) => {
    const next = {} as Record<BorrowerSeperator, boolean>
    for (const sep of borrowerOrder) {
      next[sep] = checked
    }
    onChange(next)
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-gray-700">Required by borrower</div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <button type="button" className="btn-link" onClick={() => setAll(true)}>
            Select all
          </button>
          <span className="text-gray-300" aria-hidden>
            |
          </span>
          <button type="button" className="btn-link" onClick={() => setAll(false)}>
            Unselect all
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 max-h-64 overflow-y-auto pr-1">
        {borrowerOrder.map((sep) => (
          <label key={sep} className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={record[sep]}
              onChange={(e) => setKey(sep, e.target.checked)}
            />
            <span>{labels[sep]}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
